/**
 * Контракт события task-queue `completion-act-parsed`.
 *
 * Эмитится после успешного парсинга акта в `apps/api/src/services/file-upload.ts`
 * (ветка `fileType === "completion_act"`). Фронтенд подписывается через
 * `useTaskEvents(uploadId, onEvent)` и обновляет UI результата парсинга.
 *
 * Целевое расположение в production: `packages/shared/contracts/completion-act.ts`
 * (этот файл в `specs/` — контракт-оракул для Phase 1; при реализации
 * копируется в packages/shared без изменений).
 *
 * Спецификация: [spec.md FR-031 + FR-031a](../spec.md).
 */
import { z } from "zod";
// ─── Layout variant ──────────────────────────────────────────
export const LayoutVariantSchema = z.enum([
    "standard", // нумерация 1..13 в B..N (FR-001)
    "with_code_in_a", // нумерация 1..14 в A..N
    "without_code", // нумерация 1..13 в A..M, колонка № расценки отсутствует
]);
// ─── Grand-total kind ────────────────────────────────────────
export const GrandDeclaredKindSchema = z.enum(["ИТОГО:", "ФОТ / материалы", "ВСЕГО:"]);
// ─── LineType (внутренний enum классификатора, 10 значений) ──
export const LineTypeSchema = z.enum([
    "work_line",
    "continuation_line",
    "extra_material",
    "section_header",
    "subtotal",
    "subtotal_implicit",
    "grand_total",
    "hybrid_continuation_and_material",
    "noise",
    "empty",
]);
// ─── Validation issue / warning (FR-020) ─────────────────────
export const IssueKindSchema = z.enum([
    "arithmetic_mismatch_work",
    "arithmetic_mismatch_material",
    "subtotal_mismatch_salary",
    "subtotal_mismatch_materials",
    "subtotal_mismatch_total",
    "grand_total_mismatch_fot",
    "grand_total_mismatch_materials",
]);
export const WarningKindSchema = z.enum([
    "block_missing_subtotal",
    "grand_total_not_declared",
    "hybrid_rows_high_count",
]);
/**
 * Единый формат элемента issues[] / warnings[].
 * Различие — только в допустимом наборе `kind`.
 */
export const ValidationIssueSchema = z.object({
    kind: z.string(), // IssueKind | WarningKind; валидируется отдельно по массиву
    scope: z.enum(["work", "material", "block", "grand"]),
    row: z.number().int().positive().optional(),
    /**
     * Доп. строки продолжения работы. Заполняется только для issue
     * со `scope: "work"`, когда у работы есть continuation/hybrid-строки.
     * Используется фронтендом (`GoToRowLink`) для подсказки «+N продолжений».
     */
    continuationRows: z.array(z.number().int().positive()).optional(),
    expected: z.number().optional(),
    got: z.number().optional(),
    delta: z.number().optional(),
    message: z.string(),
});
// ─── Event payload (FR-031) ──────────────────────────────────
/**
 * Полезная нагрузка события `completion-act-parsed`.
 * Эмитится через `task-queue` после успешного парсинга (включая
 * `parsed_with_issues`). При статусе `error` эмитится другое событие
 * (`completion-act-parse-failed`), здесь не описывается.
 */
export const CompletionActParsedEventSchema = z.object({
    uploadId: z.string().uuid(),
    fileId: z.number().int().positive(),
    filename: z.string(),
    // Сводка по извлечённым сущностям (FR-031)
    blocksCount: z.number().int().nonnegative(),
    worksCount: z.number().int().nonnegative(),
    materialsCount: z.number().int().nonnegative(),
    extraLaborCount: z.number().int().nonnegative(),
    // Флаги валидации (FR-019, FR-020)
    arithmeticOk: z.boolean(),
    subtotalsOk: z.boolean(),
    grandTotalOk: z.boolean(),
    // Счётчики массивов (FR-020)
    issuesCount: z.number().int().nonnegative(),
    warningsCount: z.number().int().nonnegative(),
    // Макет (FR-001)
    layoutVariant: LayoutVariantSchema,
    // Первые ≤10 элементов issues для быстрой диагностики на фронтенде (FR-034)
    issuesPreview: z.array(ValidationIssueSchema).max(10),
    // Финальный статус файла после парсинга (FR-032)
    // Переименовано: parsed_with_warnings → parsed_with_issues (T-A5.7, семантически точнее).
    fileStatus: z.enum(["parsed", "parsed_with_issues"]),
});
// ─── Parse-failed event (FR-032 для статуса error) ───────────
export const CompletionActParseFailedEventSchema = z.object({
    uploadId: z.string().uuid(),
    fileId: z.number().int().positive(),
    filename: z.string(),
    errorKind: z.enum(["empty_completion_act", "table_start_not_found", "unknown"]),
    errorMessage: z.string(),
});
// ─── ParsedAct (полный результат парсера, используется в raw_json) ──
/**
 * Полный результат `parseAct(rows)` — формат сериализации в
 * `completion_act_validation.raw_json` при `arithmetic_ok = 0` (FR-025).
 *
 * Соответствует `ParsedAct` из [pilot types.ts](../../docs/completion-act-parser-v3-handoff/06-pilot-ts-parser/src/types.ts).
 * Схема не используется в runtime-валидации, но экспорт типа нужен для
 * ad-hoc чтения `raw_json` на фронтенде / в admin-интерфейсе.
 */
export const RawMaterialSchema = z.object({
    name: z.string(),
    unit: z.string().nullable(),
    norm: z.number().nullable(),
    totalConsumption: z.number(),
    price: z.number().nullable(),
    cost: z.number(),
    excelRow: z.number().int().positive(),
});
export const RawExtraLaborSchema = z.object({
    unit: z.string().nullable(),
    qty: z.number(),
    rate: z.number(),
    total: z.number(),
    excelRow: z.number().int().positive(),
});
export const RawWorkSchema = z.object({
    code: z.string().nullable(),
    name: z.string(),
    unit: z.string().nullable(),
    quantity: z.number(),
    salaryRate: z.number().nullable(),
    salaryTotal: z.number(),
    extraLabor: z.array(RawExtraLaborSchema),
    materials: z.array(RawMaterialSchema),
    excelRow: z.number().int().positive(),
    continuationRows: z.array(z.number().int().positive()),
});
export const RawBlockSchema = z.object({
    sectionHeader: z.string().nullable(),
    sectionHeaderRow: z.number().int().positive().nullable(),
    works: z.array(RawWorkSchema),
    subtotal: z
        .object({
        salary: z.number(),
        materials: z.number(),
        total: z.number(),
        isImplicit: z.boolean(),
        excelRow: z.number().int().positive().nullable(),
    })
        .nullable(),
});
export const ValidationResultSchema = z.object({
    arithmeticOk: z.boolean(),
    subtotalsOk: z.boolean(),
    grandTotalOk: z.boolean(),
    grand: z.object({
        fotDeclared: z.number().nullable(),
        materialsDeclared: z.number().nullable(),
        totalDeclared: z.number().nullable(),
        kind: GrandDeclaredKindSchema.nullable(),
        fotComputed: z.number(),
        materialsComputed: z.number(),
    }),
    issues: z.array(ValidationIssueSchema),
    warnings: z.array(ValidationIssueSchema),
    hybridRowsCount: z.number().int().nonnegative(),
});
export const ParsedActSchema = z.object({
    layoutVariant: LayoutVariantSchema,
    tableRange: z.object({
        startRow: z.number().int().positive(),
        endRow: z.number().int().positive(),
    }),
    blocks: z.array(RawBlockSchema),
    validation: ValidationResultSchema,
});
//# sourceMappingURL=completion-act.js.map